<template>
  <div class="lprofile" dir="rtl">

    <!-- PIN overlay modal for locked profiles -->
    <ui-modal
      v-model="showPinModal"
      title="הזן קוד PIN"
      size="sm"
      :closable="true"
      @close="closePinModal"
    >
      <div class="lprofile__pin-modal">
        <p class="lprofile__pin-prompt">הפרופיל <strong>{{ lockedProfileName }}</strong> מוגן בקוד PIN.</p>

        <!-- PIN cells (visual) -->
        <div class="lprofile__pin-row" role="group" aria-label="ספרות PIN">
          <div
            v-for="(d, idx) in pinDisplayDigits"
            :key="'pd' + idx"
            class="lprofile__pin-cell"
            :class="{ 'lprofile__pin-cell--filled': d !== '' }"
            aria-hidden="true"
          >{{ d !== '' ? '•' : '' }}</div>
        </div>

        <!-- actual hidden input -->
        <input
          ref="pinInput"
          type="password"
          maxlength="4"
          inputmode="numeric"
          pattern="[0-9]*"
          v-model="enteredPin"
          class="lprofile__pin-input"
          placeholder="הקלד קוד PIN"
          autocomplete="current-password"
          aria-label="קוד PIN בן 4 ספרות"
          @input="onPinInput"
          @keydown.enter="submitPin"
        />

        <!-- reset password link -->
        <button
          type="button"
          class="lprofile__forgot"
          @click="openResetForLocked"
        >שכחתי קוד</button>

        <!-- inline error -->
        <div v-if="pinError" class="lprofile__pin-error" role="alert" aria-live="assertive">
          {{ pinError }}
        </div>
      </div>
      <template slot="footer">
        <ui-button variant="ghost"   size="lg" @click="closePinModal">ביטול</ui-button>
        <ui-button variant="primary" size="lg" :loading="enteringProfile" @click="submitPin">כניסה</ui-button>
      </template>
    </ui-modal>

    <!-- password reset modal -->
    <ui-modal
      v-model="showResetModal"
      title="איפוס סיסמה"
      size="sm"
      :closable="true"
      @close="showResetModal = false"
    >
      <div class="lprofile__reset-body">
        <p class="lprofile__reset-prompt">הזן את כתובת המייל שלך ונשלח אליך קישור לאיפוס קוד.</p>
        <ui-input
          label="כתובת מייל"
          v-model="email"
          type="email"
          placeholder="your@email.com"
          :required="true"
          :error="resetError"
        />
        <div v-if="resetSuccess" class="lprofile__reset-success" role="status">
          {{ resetSuccess }}
        </div>
      </div>
      <template slot="footer">
        <ui-button variant="ghost"   size="md" @click="showResetModal = false">ביטול</ui-button>
        <ui-button variant="primary" size="md" :loading="resetting" @click="onConfirmResetPass">שלח</ui-button>
      </template>
    </ui-modal>

    <!-- page -->
    <div class="lprofile__page">
      <h1 class="lprofile__title">נא לבחור פרופיל</h1>

      <ul class="lprofile__grid" role="list">
        <li
          v-for="p in user_data.profiles"
          :key="p.id"
          class="lprofile__card"
          :class="{ 'lprofile__card--locked': p.password && p.password !== '' }"
        >
          <button
            type="button"
            class="lprofile__card-btn"
            @click="handleProfileClick(p)"
            :aria-label="(p.name || 'הפרופיל שלך') + (p.password && p.password !== '' ? ' — פרופיל נעול' : '')"
          >
            <!-- avatar -->
            <div class="lprofile__avatar">
              <!-- locked overlay -->
              <div v-if="p.password && p.password !== ''" class="lprofile__locked-overlay" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <svg class="lprofile__avatar-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            </div>
            <span class="lprofile__name">{{ p.name || 'הפרופיל שלך' }}</span>
          </button>

          <!-- forgot link below card -->
          <button
            type="button"
            class="lprofile__forgot-link"
            @click="openResetFor(p.id)"
          >שכחתי סיסמה</button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import UiModal  from './ui/Modal.vue';
import UiButton from './ui/Button.vue';
import UiInput  from './ui/Input.vue';

export default {
  components: { UiModal, UiButton, UiInput },
  mixins: [clickaway],

  props: ['user_data', 'menu', 'profile'],

  data() {
    return {
      profile_id: '',
      email: '',
      show_user_menu: false,
      user_token: localStorage.getItem('user_token'),

      // PIN modal
      showPinModal: false,
      enteredPin: '',
      pinError: '',
      pinDisplayDigits: ['', '', '', ''],
      pendingProfile: null,
      enteringProfile: false,

      // reset modal
      showResetModal: false,
      resetting: false,
      resetError: '',
      resetSuccess: '',
    };
  },

  computed: {
    lockedProfileName() {
      return this.pendingProfile ? (this.pendingProfile.name || 'הפרופיל שלך') : '';
    },
  },

  beforeMount() {
    this.goEnter();
  },

  methods: {
    goEnter() {
      if (this.user_data.profiles.length === 1) {
        this.enter_profile(this.user_data.profiles[0].id);
      }
    },

    handleProfileClick(p) {
      if (p.password && p.password !== '') {
        this.pendingProfile = p;
        this.enteredPin = '';
        this.pinError = '';
        this.pinDisplayDigits = ['', '', '', ''];
        this.showPinModal = true;
        this.$nextTick(() => {
          if (this.$refs.pinInput) this.$refs.pinInput.focus();
        });
      } else {
        this.enter_profile(p.id);
      }
    },

    onPinInput() {
      var val = this.enteredPin.split('');
      this.pinDisplayDigits = [
        val[0] || '',
        val[1] || '',
        val[2] || '',
        val[3] || '',
      ];
    },

    submitPin() {
      if (!this.pendingProfile) return;
      if (this.enteredPin !== this.pendingProfile.password) {
        this.pinError = 'הקוד שהוזן שגוי, נסה שנית';
        return;
      }
      this.pinError = '';
      this.enteringProfile = true;
      this.api_call({
        url: 'change_profile',
        data: { id: this.pendingProfile.id },
        onComplete: (data) => {
          localStorage.setItem('user_token', data.data.data);
          window.sessionStorage.setItem('profile_id', this.pendingProfile.id);
          document.location = window.location.href;
        },
      });
    },

    closePinModal() {
      this.showPinModal = false;
      this.pendingProfile = null;
      this.enteredPin = '';
      this.pinError = '';
    },

    enter_profile(id) {
      var curr_profile = false;
      for (let i in this.user_data.profiles) {
        if (this.user_data.profiles[i].id == id) {
          curr_profile = this.user_data.profiles[i];
          break;
        }
      }
      if (curr_profile && curr_profile.password === '') {
        this.api_call({
          url: 'change_profile',
          data: { id: id },
          onComplete: (data) => {
            localStorage.setItem('user_token', data.data.data);
            window.sessionStorage.setItem('profile_id', id);
            document.location = '/';
          },
        });
      }
    },

    openResetFor(profileId) {
      this.profile_id   = profileId;
      this.email        = '';
      this.resetError   = '';
      this.resetSuccess = '';
      this.showResetModal = true;
    },

    openResetForLocked() {
      if (this.pendingProfile) {
        this.profile_id = this.pendingProfile.id;
      }
      this.email        = '';
      this.resetError   = '';
      this.resetSuccess = '';
      this.showPinModal = false;
      this.showResetModal = true;
    },

    onConfirmResetPass() {
      if (!this.email || !this.email.trim()) {
        this.resetError = 'נא להזין כתובת מייל';
        return;
      }
      this.resetError = '';
      this.resetting = true;
      this.api_call({
        url: 'password_profile_reset',
        data: { email: this.email, profile_id: this.profile_id },
        onComplete: (data) => {
          this.resetting = false;
          if (data.data.data) {
            this.resetSuccess = 'הסיסמה נשלחה למייל שהוזן';
          } else {
            this.resetError = 'המייל לא נמצא במערכת';
          }
        },
      });
    },

    logout() {
      localStorage.removeItem('user_token');
      document.location = '/';
    },

    away() {
      this.show_user_menu = false;
    },
  },
};
</script>

<style scoped lang="scss">
.lprofile {
  min-height: 100vh;
  background: var(--color-bg);
  font-family: var(--font-hebrew);
  padding-block: var(--space-12);
}

.lprofile__page {
  max-width: 900px;
  margin-inline: auto;
  padding-inline: var(--space-6);
}

.lprofile__title {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.4px;
  text-align: center;
  margin-block-end: var(--space-12);
}

/* grid */
.lprofile__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--space-6);
  list-style: none;
  padding: 0;
  margin: 0;
  justify-items: center;
}

/* card */
.lprofile__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

.lprofile__card-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: var(--font-hebrew);
  text-align: center;
}

.lprofile__card-btn:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 4px;
  border-radius: var(--radius-sm);
}

/* avatar */
.lprofile__avatar {
  width: 140px;
  height: 140px;
  border-radius: var(--radius-md);
  background: var(--color-surface3);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color var(--duration-base) var(--easing-default),
              transform var(--duration-base) var(--easing-default);
}

.lprofile__card-btn:hover .lprofile__avatar {
  border-color: var(--color-text);
  transform: scale(1.03);
}

.lprofile__avatar-icon {
  color: var(--color-textDim);
}

/* locked overlay */
.lprofile__locked-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  z-index: 1;
}

.lprofile__name {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lprofile__forgot-link {
  font-size: 12px;
  color: var(--color-textMuted);
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-3) var(--space-4);
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-hebrew);
  text-decoration: underline;
  transition: color var(--duration-fast) var(--easing-default);
}

.lprofile__forgot-link:hover {
  color: var(--color-text);
}

.lprofile__forgot-link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  border-radius: 2px;
}

/* PIN modal content */
.lprofile__pin-modal {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
}

.lprofile__pin-prompt {
  font-size: 14px;
  color: var(--color-textMuted);
  text-align: center;
  margin: 0;
}

.lprofile__pin-row {
  display: flex;
  gap: var(--space-3);
}

.lprofile__pin-cell {
  width: 56px;
  height: 64px;
  border-radius: var(--radius-sm);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  font-family: var(--font-mono);
  transition: border-color var(--duration-fast) var(--easing-default);
}

.lprofile__pin-cell--filled {
  border-color: var(--color-text);
}

.lprofile__pin-input {
  width: 100%;
  height: 48px;
  padding-inline: 14px;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: 15px;
  font-family: var(--font-hebrew);
  outline: none;
  transition: border-color var(--duration-fast) var(--easing-default),
              box-shadow var(--duration-fast) var(--easing-default);
  text-align: center;
  letter-spacing: 4px;
}

.lprofile__pin-input:focus {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(225, 29, 42, 0.2);
}

.lprofile__pin-error {
  font-size: 13px;
  color: var(--color-danger);
  text-align: center;
}

.lprofile__forgot {
  font-size: 13px;
  color: var(--color-textMuted);
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-hebrew);
  text-decoration: underline;
  padding: var(--space-2) 0;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  transition: color var(--duration-fast) var(--easing-default);
}

.lprofile__forgot:hover {
  color: var(--color-text);
}

.lprofile__forgot:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  border-radius: 2px;
}

/* reset modal body */
.lprofile__reset-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  font-family: var(--font-hebrew);
}

.lprofile__reset-prompt {
  font-size: 14px;
  color: var(--color-textMuted);
  margin: 0;
}

.lprofile__reset-success {
  font-size: 13px;
  color: var(--color-success);
  padding: 10px 14px;
  background: rgba(31,174,107,0.1);
  border: 1px solid rgba(31,174,107,0.3);
  border-radius: var(--radius-sm);
}

@media (prefers-reduced-motion: reduce) {
  .lprofile__avatar,
  .lprofile__pin-cell,
  .lprofile__pin-input,
  .lprofile__forgot-link {
    transition: none;
  }
  .lprofile__card-btn:hover .lprofile__avatar {
    transform: none;
  }
}
</style>
